import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Image as ImageIcon, X, Save, FileText, GripVertical, ChevronUp, ChevronDown, Layers } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const CONTENT_TYPES = [
  { value: 'text',  label: 'Texto / Markdown' },
  { value: 'video', label: 'Vídeo (YouTube / Drive)' },
  { value: 'pdf',   label: 'PDF (enlace externo)' },
];

// Inline editor for a single sub-page (page-level form, no nested modal)
const SubPageForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: initial?.title || '',
    image_url: initial?.image_url || '',
    image_position: initial?.image_position || 'center center',
    content_type: initial?.content_type || 'text',
    content_text: initial?.content_text || '',
    content_url: initial?.content_url || '',
  });
  return (
    <div className="border border-[#D4A84B]/30 bg-[#fdfbf5] rounded-xl p-4 space-y-3" data-testid="subpage-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2 space-y-1.5">
          <label className="block text-[11px] font-medium text-gray-600">Título de la página</label>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm" data-testid="subpage-title" />
        </div>
        <div className="space-y-1.5">
          <label className="block text-[11px] font-medium text-gray-600">Posición imagen</label>
          <select value={form.image_position} onChange={e => setForm({ ...form, image_position: e.target.value })} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white">
            <option value="center center">Centro</option>
            <option value="center top">Arriba</option>
            <option value="center bottom">Abajo</option>
            <option value="left center">Izquierda</option>
            <option value="right center">Derecha</option>
          </select>
        </div>
      </div>
      <div className="space-y-1.5">
        <label className="block text-[11px] font-medium text-gray-600">Imagen (Cloudinary URL)</label>
        <input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-mono" placeholder="https://res.cloudinary.com/..." />
      </div>
      <div className="space-y-1.5">
        <label className="block text-[11px] font-medium text-gray-600">Tipo de contenido</label>
        <select value={form.content_type} onChange={e => setForm({ ...form, content_type: e.target.value })} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white">
          {CONTENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      {form.content_type === 'text' ? (
        <div className="space-y-1.5">
          <label className="block text-[11px] font-medium text-gray-600">Contenido (Markdown)</label>
          <textarea rows={6} value={form.content_text} onChange={e => setForm({ ...form, content_text: e.target.value })} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-mono" placeholder="# Encabezado&#10;&#10;Texto en **markdown**" />
        </div>
      ) : (
        <div className="space-y-1.5">
          <label className="block text-[11px] font-medium text-gray-600">{form.content_type === 'video' ? 'URL del vídeo' : 'URL del PDF'}</label>
          <input value={form.content_url} onChange={e => setForm({ ...form, content_url: e.target.value })} className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-mono" />
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-xs text-gray-600 hover:bg-white rounded-lg">Cancelar</button>
        <button
          onClick={() => { if (!form.title.trim()) { toast.error('Pon un título'); return; } onSave(form); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#D4A84B] hover:bg-[#b89220] text-white rounded-lg"
          data-testid="subpage-save"
        >
          <Save className="w-3.5 h-3.5" /> Guardar página
        </button>
      </div>
    </div>
  );
};

// Sub-pages manager: list + reorder + create/edit/delete
const SubPagesManager = ({ courseSlug, blockId, variant = 'preliminary', initialSubPages, onChange }) => {
  const [subs, setSubs] = useState([...(initialSubPages || [])].sort((a, b) => (a.order || 0) - (b.order || 0)));
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const endpoint = variant === 'practice' ? 'practice-modules' : 'preliminary-modules';

  useEffect(() => { onChange?.(subs); /* eslint-disable-next-line */ }, [subs]);

  const create = async (form) => {
    try {
      const r = await api.post(`/admin/special-courses/${courseSlug}/${endpoint}/${blockId}/sub-pages`, form);
      setSubs(prev => [...prev, r.data].sort((a, b) => (a.order || 0) - (b.order || 0)));
      setCreating(false);
      toast.success('Página añadida');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al añadir'); }
  };

  const update = async (subId, form) => {
    try {
      await api.put(`/admin/special-courses/${courseSlug}/${endpoint}/${blockId}/sub-pages/${subId}`, form);
      setSubs(prev => prev.map(p => p.id === subId ? { ...p, ...form } : p));
      setEditingId(null);
      toast.success('Página actualizada');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
  };

  const remove = async (sub) => {
    if (!window.confirm(`Eliminar la página "${sub.title}"?`)) return;
    try {
      await api.delete(`/admin/special-courses/${courseSlug}/${endpoint}/${blockId}/sub-pages/${sub.id}`);
      setSubs(prev => prev.filter(p => p.id !== sub.id));
      toast.success('Eliminada');
    } catch { toast.error('Error al eliminar'); }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= subs.length) return;
    const next = [...subs];
    [next[idx], next[target]] = [next[target], next[idx]];
    next.forEach((p, i) => { p.order = i + 1; });
    setSubs(next);
    try {
      await api.put(`/admin/special-courses/${courseSlug}/${endpoint}/${blockId}/sub-pages/reorder`, { order: next.map(p => p.id) });
    } catch { toast.error('Error al reordenar'); }
  };

  return (
    <div className="border-t border-gray-100 pt-5 mt-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm font-medium text-gray-800 flex items-center gap-1.5"><Layers className="w-4 h-4 text-[#D4A84B]" /> Sub-páginas (opcional)</h4>
          <p className="text-[11px] text-gray-400">Si añades sub-páginas, la alumna verá un índice de tarjetas numeradas. Si no, verá el contenido único de arriba.</p>
        </div>
        {!creating && !editingId && (
          <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs bg-white border border-[#D4A84B]/40 text-[#8a7428] hover:bg-[#fdfbf5] rounded-lg" data-testid="subpage-new">
            <Plus className="w-3.5 h-3.5" /> Añadir página
          </button>
        )}
      </div>

      {/* Pages list as numbered cards */}
      {subs.length === 0 && !creating && (
        <p className="text-xs text-gray-400 italic py-4 text-center">Sin sub-páginas. Esta esfera mostrará el contenido único definido arriba.</p>
      )}
      {subs.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          {subs.map((p, idx) => (
            <div key={p.id} className="bg-white border border-gray-100 rounded-lg p-3 flex gap-3" data-testid={`subpage-card-${idx}`}>
              <div className="relative w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-[#FBF8F1] border border-[#D4A84B]/30">
                {p.image_url ? (
                  <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" style={{ objectPosition: p.image_position || 'center center' }} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#D4A84B]/50"><ImageIcon className="w-5 h-5" /></div>
                )}
                <span className="absolute -top-1.5 -left-1.5 w-5 h-5 rounded-full bg-[#D4A84B] text-white text-[10px] font-medium flex items-center justify-center">{idx + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{p.title}</p>
                <p className="text-[10px] text-gray-400">
                  {p.content_type === 'text' ? (p.content_text?.trim() ? 'Contenido escrito' : 'Sin contenido') : (p.content_url ? `Enlace ${p.content_type}` : 'Sin enlace')}
                </p>
                <div className="mt-1.5 flex items-center gap-0.5">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-gray-100 text-gray-400 disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => move(idx, 1)} disabled={idx === subs.length - 1} className="p-1 rounded hover:bg-gray-100 text-gray-400 disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                  <span className="text-[9px] text-gray-300 ml-0.5"><GripVertical className="w-2.5 h-2.5 inline" /></span>
                  <button onClick={() => setEditingId(p.id)} className="ml-auto p-1 rounded hover:bg-[#fdfbf5] text-gray-500 hover:text-[#D4A84B]" data-testid={`subpage-edit-${idx}`}><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(p)} className="p-1 rounded hover:bg-red-50 text-gray-500 hover:text-red-600" data-testid={`subpage-delete-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <SubPageForm
          initial={null}
          onSave={create}
          onCancel={() => setCreating(false)}
        />
      )}
      {editingId && (
        <SubPageForm
          initial={subs.find(p => p.id === editingId)}
          onSave={(form) => update(editingId, form)}
          onCancel={() => setEditingId(null)}
        />
      )}
    </div>
  );
};

const EditModal = ({ block, courseSlug, variant, onClose, onSave }) => {
  const [form, setForm] = useState({
    title: block?.title || '',
    image_url: block?.image_url || '',
    image_position: block?.image_position || 'center center',
    content_type: block?.content_type || 'text',
    content_text: block?.content_text || '',
    content_url: block?.content_url || '',
    is_favorites_block: !!block?.is_favorites_block,
  });
  const [saving, setSaving] = useState(false);
  const isPractice = variant === 'practice';

  const submit = async () => {
    if (!form.title.trim()) { toast.error('Pon un título'); return; }
    setSaving(true);
    try {
      await onSave(form);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="text-lg font-heading text-gray-800">Editar esfera {isPractice ? 'práctica' : 'introductoria'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {isPractice && (
            <label className="flex items-start gap-3 p-3 rounded-xl border border-[#D4A84B]/30 bg-[#fdfbf5] cursor-pointer">
              <input type="checkbox" checked={form.is_favorites_block} onChange={e => setForm({ ...form, is_favorites_block: e.target.checked })} className="mt-0.5 w-4 h-4 accent-[#D4A84B]" data-testid="prelim-edit-favorites-toggle" />
              <div className="text-sm">
                <p className="font-medium text-gray-800">Esfera de "Tus semillas de luz"</p>
                <p className="text-[11px] text-gray-500 leading-relaxed">Esta esfera mostrará automáticamente las frases que cada alumna haya guardado al subrayar texto. Solo puede haber una por curso (al activarla aquí se desactiva en cualquier otra esfera). El contenido fijo y las sub-páginas se ocultan en esta esfera.</p>
              </div>
            </label>
          )}
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-600">Título</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="prelim-edit-title" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2 space-y-1.5">
              <label className="block text-xs font-medium text-gray-600">URL de la imagen (Cloudinary u otra)</label>
              <input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono" placeholder="https://res.cloudinary.com/..." data-testid="prelim-edit-image" />
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-gray-600">Posición de la imagen</label>
              <select value={form.image_position} onChange={e => setForm({ ...form, image_position: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white">
                <option value="center center">Centro</option>
                <option value="center top">Arriba</option>
                <option value="center bottom">Abajo</option>
                <option value="left center">Izquierda</option>
                <option value="right center">Derecha</option>
              </select>
            </div>
          </div>
          {form.image_url && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-[#D4A84B]/40 flex-shrink-0">
                <img src={form.image_url} alt="Vista previa" className="w-full h-full object-cover" style={{ objectPosition: form.image_position }} />
              </div>
              <p className="text-xs text-gray-500">Vista previa de la esfera</p>
            </div>
          )}
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-600">Tipo de contenido</label>
            <select value={form.content_type} onChange={e => setForm({ ...form, content_type: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white" data-testid="prelim-edit-type">
              {CONTENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {form.content_type === 'text' ? (
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-gray-600">Contenido (Markdown)</label>
              <textarea rows={10} value={form.content_text} onChange={e => setForm({ ...form, content_text: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono" placeholder="# Encabezado&#10;&#10;Texto en **markdown** con enlaces, listas, etc." data-testid="prelim-edit-text" />
              <p className="text-[11px] text-gray-400">Admite formato Markdown (encabezados, negritas, listas, enlaces, imágenes).</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-gray-600">
                {form.content_type === 'video' ? 'URL del vídeo (YouTube o Drive)' : 'URL del PDF'}
              </label>
              <input value={form.content_url} onChange={e => setForm({ ...form, content_url: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono" placeholder={form.content_type === 'video' ? 'https://drive.google.com/file/d/.../view o https://youtu.be/...' : 'https://...'} />
            </div>
          )}

          {/* Sub-pages manager — only available once the block exists, and not for the favorites block */}
          {block?.id && !form.is_favorites_block && (
            <SubPagesManager
              courseSlug={courseSlug}
              blockId={block.id}
              variant={variant}
              initialSubPages={block.sub_pages || []}
            />
          )}
          {!block?.id && (
            <div className="border-t border-gray-100 pt-4 mt-4">
              <p className="text-[11px] text-gray-400 italic">
                Guarda la esfera primero para poder añadirle sub-páginas.
              </p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-gray-100 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">Cancelar</button>
          <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#D4A84B] hover:bg-[#b89220] text-white rounded-lg disabled:opacity-50" data-testid="prelim-edit-save">
            <Save className="w-4 h-4" /> {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function AdminPreliminarySpheres({ courseSlug, variant = 'preliminary', title = 'Esferas introductorias', description = 'Aparecen como círculos antes del programa formativo. Solo se muestran a las alumnas inscritas en Límites.', onTitleChange }) {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(title);
  const endpoint = variant === 'practice' ? 'practice-modules' : 'preliminary-modules';

  useEffect(() => { setTitleDraft(title); }, [title]);

  const load = async () => {
    try {
      const r = await api.get(`/admin/special-courses/${courseSlug}/${endpoint}`);
      setItems(r.data || []);
    } catch {
      toast.error('Error al cargar esferas');
      setItems([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [courseSlug, variant]);

  const saveTitle = async () => {
    const newTitle = (titleDraft || '').trim() || title;
    try {
      const field = variant === 'practice' ? 'practice_blocks_section_title' : 'preliminary_blocks_section_title';
      await api.put(`/admin/special-courses/${courseSlug}/section-titles`, { [field]: newTitle });
      toast.success('Título actualizado');
      setEditingTitle(false);
      onTitleChange?.(newTitle);
    } catch { toast.error('Error al guardar el título'); }
  };

  const create = async (form) => {
    try {
      await api.post(`/admin/special-courses/${courseSlug}/${endpoint}`, form);
      toast.success('Esfera creada');
      setCreating(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear'); }
  };

  const update = async (form) => {
    try {
      await api.put(`/admin/special-courses/${courseSlug}/${endpoint}/${editing.id}`, form);
      toast.success('Cambios guardados');
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
  };

  const remove = async (b) => {
    if (!window.confirm(`Eliminar la esfera "${b.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/admin/special-courses/${courseSlug}/${endpoint}/${b.id}`);
      toast.success('Eliminada');
      load();
    } catch { toast.error('Error al eliminar'); }
  };

  const moveOrder = async (idx, dir) => {
    if (!items) return;
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next);
    try {
      await api.put(`/admin/special-courses/${courseSlug}/${endpoint}/reorder`, { order: next.map(b => b.id) });
    } catch { toast.error('Error al reordenar'); load(); }
  };

  if (!items) return null;

  const dataTestId = variant === 'practice' ? 'admin-practice-spheres' : 'admin-preliminary-spheres';
  const cardTestPrefix = variant === 'practice' ? 'admin-practice-card' : 'admin-prelim-card';
  const emptyText = variant === 'practice' ? 'Aún no hay esferas prácticas. Crea la primera.' : 'Aún no hay esferas introductorias. Crea la primera.';

  return (
    <div className="mb-8" data-testid={dataTestId}>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          {editingTitle ? (
            <div className="flex items-center gap-2">
              <input
                value={titleDraft}
                onChange={e => setTitleDraft(e.target.value)}
                className="flex-1 px-3 py-1.5 border border-[#D4A84B]/40 rounded-lg text-lg font-heading text-gray-800 bg-white focus:outline-none focus:border-[#D4A84B]"
                placeholder={variant === 'practice' ? 'Esferas prácticas' : 'Esferas introductorias'}
                autoFocus
              />
              <button onClick={saveTitle} className="px-3 py-1.5 text-xs bg-[#D4A84B] hover:bg-[#b89220] text-white rounded-lg">Guardar</button>
              <button onClick={() => { setEditingTitle(false); setTitleDraft(title); }} className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded-lg">Cancelar</button>
            </div>
          ) : (
            <>
              <button onClick={() => setEditingTitle(true)} className="group inline-flex items-center gap-2 text-left">
                <h2 className="text-lg font-heading text-gray-800 group-hover:text-[#D4A84B] transition-colors">{title}</h2>
                <Edit2 className="w-3.5 h-3.5 text-gray-300 group-hover:text-[#D4A84B] transition-colors" />
              </button>
              <p className="text-xs text-gray-400">{description}</p>
            </>
          )}
        </div>
        {!editingTitle && (
          <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[#D4A84B] hover:bg-[#b89220] text-white rounded-lg" data-testid={variant === 'practice' ? 'admin-practice-new' : 'admin-prelim-new'}>
            <Plus className="w-4 h-4" /> Nueva esfera
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-10 bg-white border border-dashed border-gray-200 rounded-xl text-sm text-gray-500">
          {emptyText}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((b, idx) => (
            <div key={b.id} className="group bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow" data-testid={`${cardTestPrefix}-${idx}`}>
              <div className="flex flex-col items-center text-center">
                <div className="relative w-24 h-24 rounded-full overflow-hidden bg-[#FBF8F1] border-2 border-[#D4A84B]/30 mb-3">
                  {b.image_url ? (
                    <img src={b.image_url} alt={b.title} className="w-full h-full object-cover" style={{ objectPosition: b.image_position || 'center center' }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#D4A84B]/50">
                      <ImageIcon className="w-8 h-8" />
                    </div>
                  )}
                </div>
                <p className="font-medium text-gray-800 mb-1">{b.title}</p>
                {b.is_favorites_block ? (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#8a7428] bg-[#fdfbf5] border border-[#D4A84B]/30 px-2 py-0.5 rounded-full">★ Tus semillas de luz</span>
                ) : (
                  <p className="text-[11px] text-gray-400 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {b.content_type === 'text' ? (b.content_text?.trim() ? 'Contenido escrito' : 'Sin contenido') : (b.content_url ? `Enlace ${b.content_type}` : 'Sin enlace')}
                  </p>
                )}
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <button onClick={() => moveOrder(idx, -1)} disabled={idx === 0} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent" title="Subir"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => moveOrder(idx, 1)} disabled={idx === items.length - 1} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent" title="Bajar"><ChevronDown className="w-4 h-4" /></button>
                  <span className="ml-1 inline-flex items-center text-[10px] text-gray-400"><GripVertical className="w-3 h-3" /> Orden {idx + 1}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEditing(b)} className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-[#D4A84B]" title="Editar" data-testid={`${cardTestPrefix === 'admin-prelim-card' ? 'admin-prelim-edit' : 'admin-practice-edit'}-${idx}`}><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => remove(b)} className="p-1.5 rounded hover:bg-red-50 text-gray-500 hover:text-red-600" title="Eliminar" data-testid={`${cardTestPrefix === 'admin-prelim-card' ? 'admin-prelim-delete' : 'admin-practice-delete'}-${idx}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <EditModal block={null} courseSlug={courseSlug} variant={variant} onClose={() => setCreating(false)} onSave={create} />
      )}
      {editing && (
        <EditModal block={editing} courseSlug={courseSlug} variant={variant} onClose={() => setEditing(null)} onSave={update} />
      )}
    </div>
  );
}
